import { Checkbox, type CheckboxProps } from '@progress/kendo-react-inputs'
import { useAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import * as R from 'remeda'

import { useTurnOnAtom } from '~/hooks/jotai'
import { type CustomerAttribute } from '~/hooks/useSurveyQuery'

import {
  columnCustomerAttributeIdsAtom,
  isPendingTabulationAtom,
  sortValueAtom,
} from '../atoms'

import styles from './CustomerAttributeCheckbox.css'

export type CustomerAttributeCheckboxProps = {
  customerAttribute: CustomerAttribute
}

export const CustomerAttributeCheckbox = ({
  customerAttribute,
}: CustomerAttributeCheckboxProps) => {
  const [columnCustomerAttributeIds, setColumnCustomerAttributeIds] = useAtom(
    columnCustomerAttributeIdsAtom,
  )
  const checked = React.useMemo(
    () => columnCustomerAttributeIds.includes(customerAttribute.id),
    [customerAttribute.id, columnCustomerAttributeIds],
  )

  const resetSortValue = useResetAtom(sortValueAtom)
  const suspendTabulation = useTurnOnAtom(isPendingTabulationAtom)
  const handleChange = React.useCallback<EH<CheckboxProps, 'onChange'>>(
    (event) => {
      resetSortValue()
      setColumnCustomerAttributeIds((prev) =>
        event.value
          ? R.concat(prev, [customerAttribute.id])
          : R.difference(prev, [customerAttribute.id]),
      )
      suspendTabulation()
    },
    [
      customerAttribute.id,
      resetSortValue,
      setColumnCustomerAttributeIds,
      suspendTabulation,
    ],
  )

  return (
    <Checkbox
      key={customerAttribute.id}
      checked={checked}
      label={customerAttribute.name}
      labelClassName={styles.label}
      onChange={handleChange}
    />
  )
}
